import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppShell from './components/AppShell';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import EODFormPage from './pages/EODFormPage';
import SaleFormPage from './pages/SaleFormPage';
import RecordShowPage from './pages/RecordShowPage';
import SalesRecordsPage from './pages/SalesRecordsPage';
import EODRecordsPage from './pages/EODRecordsPage';
import AdminLocationsPage from './pages/AdminLocationsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminDropdownsPage from './pages/AdminDropdownsPage';
import ImportPage from './pages/ImportPage';
import SuperAdminPage from './pages/SuperAdminPage';
import MarketingCalculatorPage from './pages/MarketingCalculatorPage';
import LocationComparisonPage from './pages/LocationComparisonPage';
import AuditLogPage from './pages/AuditLogPage';
import NotificationsPage from './pages/NotificationsPage';
import EODCallCenterPage from './pages/EODCallCenterPage';
import RegisterPage from './pages/RegisterPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import CheckoutCancelPage from './pages/CheckoutCancelPage';
import CheckoutResumePage from './pages/CheckoutResumePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PublicFormsPage from './pages/PublicFormsPage';
import SettingsPage from './pages/SettingsPage';
import GoalsPage from './pages/GoalsPage';
import FlowCalculatorPage from './pages/FlowCalculatorPage';
import SubscriptionPage from './pages/SubscriptionPage';
import CoachDashboardPage from './pages/CoachDashboardPage';
import CoachRegisterPage from './pages/CoachRegisterPage';
import './App.css';

// Gate unpaid users to the payment page, and redirect coaches to their dashboard
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="flex items-center justify-center min-h-screen" style={{ background: '#0A0F1E' }}>
      <div className="text-blue-400 text-sm font-heading animate-pulse">Loading…</div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  
  // Redirect coaches to their dedicated dashboard
  if (user.role === 'coach' && window.location.pathname === '/') {
    return <Navigate to="/coach" replace />;
  }
  
  // Block dashboard access if subscription payment is pending
  if (user.requires_payment && user.role !== 'super_admin') {
    return <Navigate to="/checkout/resume" replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/coach/register" element={<CoachRegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
      <Route path="/checkout/resume" element={<CheckoutResumePage />} />
      <Route path="/forms/:token" element={<PublicFormsPage />} />
      <Route path="/forms/:token/eod" element={<PublicFormsPage />} />
      <Route path="/forms/:token/callcenter" element={<PublicFormsPage />} />
      <Route path="/forms/:token/sale" element={<PublicFormsPage />} />
      {/* Coach Dashboard (standalone, no AppShell) */}
      <Route path="/coach" element={
        <ProtectedRoute roles={['coach']}>
          <CoachDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/" element={
        <ProtectedRoute>
          <AppShell />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardPage />} />
        <Route path="eod/new" element={<EODFormPage />} />
        <Route path="eod/callcenter" element={<EODCallCenterPage />} />
        <Route path="eod/records" element={<EODRecordsPage />} />
        <Route path="sales/new" element={<SaleFormPage />} />
        <Route path="sales/records" element={<SalesRecordsPage />} />
        <Route path="shows/new" element={<RecordShowPage />} />
        <Route path="admin/locations" element={
          <ProtectedRoute roles={['admin', 'super_admin']}><AdminLocationsPage /></ProtectedRoute>
        } />
        <Route path="admin/users" element={
          <ProtectedRoute roles={['admin', 'super_admin']}><AdminUsersPage /></ProtectedRoute>
        } />
        <Route path="admin/dropdowns" element={
          <ProtectedRoute roles={['admin', 'super_admin']}><AdminDropdownsPage /></ProtectedRoute>
        } />
        <Route path="admin/import" element={
          <ProtectedRoute roles={['admin', 'super_admin']}><ImportPage /></ProtectedRoute>
        } />
        <Route path="marketing/calculator" element={
          <ProtectedRoute roles={['admin', 'super_admin']}><MarketingCalculatorPage /></ProtectedRoute>
        } />
        <Route path="locations/compare" element={
          <ProtectedRoute roles={['admin', 'super_admin']}><LocationComparisonPage /></ProtectedRoute>
        } />
        <Route path="admin/audit-log" element={
          <ProtectedRoute roles={['admin', 'super_admin']}><AuditLogPage /></ProtectedRoute>
        } />
        <Route path="admin/notifications" element={
          <ProtectedRoute roles={['admin', 'super_admin']}><NotificationsPage /></ProtectedRoute>
        } />
        <Route path="calculator/flow" element={<FlowCalculatorPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="goals" element={
          <ProtectedRoute roles={['admin', 'super_admin']}><GoalsPage /></ProtectedRoute>
        } />
        <Route path="superadmin" element={
          <ProtectedRoute roles={['super_admin']}><SuperAdminPage /></ProtectedRoute>
        } />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}
